import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import { STUDIO_DOWNLOAD_BASE_URL as DOWNLOAD_BASE_URL, STUDIO_LATEST_DOWNLOADS as LATEST_FALLBACK } from 'utils/installLinks';
import './DownloadPage.css';

const DOWNLOAD_MANIFEST_URL = `${DOWNLOAD_BASE_URL}/download/latest/manifest.json`;
const RELEASES_INDEX_URL = `${DOWNLOAD_BASE_URL}/download/releases.json`;

// LATEST_FALLBACK (utils/installLinks): the download/latest/ files, used until the manifest fetch lands.

const PLATFORM_LABELS = [
  { key: 'macArm',   label: 'macOS (Apple Silicon)' },
  { key: 'macIntel', label: 'macOS (Intel)' },
  { key: 'winX64',   label: 'Windows (x64)' },
  { key: 'winArm',   label: 'Windows (arm64)' },
];

const encodeKeyAsUrl = (key) =>
  String(key || '')
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');

const isInstallableArtifact = (file) =>
  /\.(dmg|exe|zip|appimage|deb|rpm)$/i.test(String(file || ''));

// Pull the right artifact out of a release's `artifacts` array per platform.
const pickArtifactForPlatform = (artifacts, platformKey) => {
  if (!Array.isArray(artifacts)) return null;
  const matchers = {
    macArm:   /mac-arm64\.(dmg|zip)$/i,
    macIntel: /mac-(x64|intel)\.(dmg|zip)$/i,
    winX64:   /win[-_]x64\.(exe|zip|msi)$/i,
    winArm:   /win[-_]arm64\.(exe|zip|msi)$/i,
  };
  const matcher = matchers[platformKey];
  if (!matcher) return null;
  // Prefer the canonical extension (.dmg / .exe) over .zip when both exist.
  const list = artifacts.filter((a) => matcher.test(String(a?.file || '')));
  if (list.length === 0) return null;
  return (
    list.find((a) => /\.(dmg|exe)$/i.test(String(a.file))) || list[0]
  );
};

const DownloadPage = () => {
  // Latest release's per-platform URLs (fed by manifest.json).
  const [latest, setLatest] = useState({
    version: null,
    releasedAt: null,
    macArm:   LATEST_FALLBACK.macArm,
    macIntel: LATEST_FALLBACK.macIntel,
    winX64:   LATEST_FALLBACK.winX64,
    winArm:   LATEST_FALLBACK.winArm,
  });
  // All past releases from releases.json. The first entry IS the latest;
  // we display it as a separate hero, then list the rest below.
  const [allReleases, setAllReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hydrate the latest manifest. Same logic as ConnectPage's hydration —
  // duplicated rather than shared because the two pages use the data
  // differently and they may diverge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(DOWNLOAD_MANIFEST_URL, { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json();
        const artifacts = (payload?.artifacts || []).filter(
          (a) => a && !String(a.file || '').endsWith('.blockmap'),
        );
        if (cancelled || artifacts.length === 0) return;

        const pick = (key) => {
          const a = pickArtifactForPlatform(artifacts, key);
          if (!a) return LATEST_FALLBACK[key];
          if (a.versionedKey) return `${DOWNLOAD_BASE_URL}/${encodeKeyAsUrl(a.versionedKey)}`;
          if (a.latestKey)    return `${DOWNLOAD_BASE_URL}/${encodeKeyAsUrl(a.latestKey)}`;
          if (a.file)         return `${DOWNLOAD_BASE_URL}/download/latest/${encodeURIComponent(a.file)}`;
          return LATEST_FALLBACK[key];
        };

        setLatest({
          version: payload?.version || null,
          releasedAt: payload?.generatedAt || null,
          macArm:   pick('macArm'),
          macIntel: pick('macIntel'),
          winX64:   pick('winX64'),
          winArm:   pick('winArm'),
        });
      } catch {
        // Manifest not available — fallback URLs in initial state still work.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the full releases index. Empty until release-binary.mjs runs the
  // updated upload step at least twice (first run creates the index with one
  // entry; second run is when "past versions" actually exists).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(RELEASES_INDEX_URL, { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json();
        if (Array.isArray(payload?.releases) && !cancelled) {
          setAllReleases(payload.releases);
        }
      } catch {
        // Index not yet generated — page just shows the latest section.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pastReleases = allReleases.length > 1 ? allReleases.slice(1) : [];

  return (
    <>
      <Helmet>
        <title>Download Teamy Studio</title>
        <meta
          name="description"
          content="Download Teamy Studio for macOS, Windows, and Linux. Latest version plus past releases."
        />
      </Helmet>
      <div className="download-wrapper">
        <PublicNav transparent />
        <main className="download-page">
          <div className="download-container">
            <header className="download-hero">
              <h1>Download Teamy Studio</h1>
              <p className="download-hero__sub">
                Desktop UI for visualising live test runs and replaying past
                sessions. Free, code-signed, with auto-updates.
              </p>
              {latest.version && (
                <p className="download-hero__version">
                  Latest: <strong>v{latest.version}</strong>
                  {latest.releasedAt
                    ? ` — released ${new Date(latest.releasedAt).toLocaleDateString()}`
                    : ''}
                </p>
              )}
            </header>

            <section className="download-section">
              <h2 className="download-section__heading">Get the latest</h2>
              <div className="download-platforms">
                {PLATFORM_LABELS.map(({ key, label }) => (
                  <a
                    key={key}
                    className="download-platform-btn"
                    href={latest[key] || LATEST_FALLBACK[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="download-platform-btn__label">{label}</span>
                    <span className="download-platform-btn__hint">Download</span>
                  </a>
                ))}
              </div>
              <p className="download-section__note">
                Verify your download against the
                {' '}
                <a
                  href={`${DOWNLOAD_BASE_URL}/download/latest/checksums.sha256.txt`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SHA-256 checksums
                </a>
                .
              </p>
            </section>

            <section className="download-section">
              <h2 className="download-section__heading">Previous versions</h2>
              {loading ? (
                <p className="download-section__note">Loading…</p>
              ) : pastReleases.length === 0 ? (
                <p className="download-section__note">
                  No previous versions are listed yet. They will appear here as
                  new releases ship.
                </p>
              ) : (
                <ul className="download-past-list">
                  {pastReleases.map((release) => (
                    <li className="download-past-item" key={release.version}>
                      <div className="download-past-item__head">
                        <span className="download-past-item__version">
                          v{release.version}
                        </span>
                        <span className="download-past-item__date">
                          {release.releasedAt
                            ? new Date(release.releasedAt).toLocaleDateString()
                            : ''}
                        </span>
                      </div>
                      <ul className="download-past-item__files">
                        {(release.artifacts || [])
                          .filter((a) => isInstallableArtifact(a?.file))
                          .map((a) => (
                            <li key={a.file}>
                              <a
                                href={`${DOWNLOAD_BASE_URL}/${encodeKeyAsUrl(a.versionedKey || '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {a.file}
                              </a>
                            </li>
                          ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>
    </>
  );
};

export default DownloadPage;
